// client/src/components/pages/LexiStar/StatusBoard/statusLanguage.ts (converted from .js, ST-00253)
//
// LexiStar status language (LS-09 §4, LS-04 §3) for the control-tower pages:
// the two status axes (motion state, operational context), their combined
// display label (FR-STAT-002), the state palette, trip stages and event
// severity.
//
// Colour is state, and only state (LS-09 §1.1): these hues are reserved for
// asset/trip state and never used for chrome (chrome uses primary/slate).
// Never colour alone: every tone carries a glyph and a text label.
//
// Pill classes are literal strings so Tailwind's JIT sees them. The hue scales
// flip lightness in dark mode (tailwind.config.js), so the dark-blue pill uses
// text-blue-50 rather than text-white to keep its contrast in both themes.
//
// ST-00253 (the control room; converted to TypeScript in the same change, every export and
// value as before): two tones join the palette — `offline` (the hollow "no live data" look the
// pills already used, now a named tone) and `breakdown` (rose: the maintenance module ST-00249
// separates a breakdown from planned maintenance, which keeps the purple `maintenance` tone).

import type { ComponentType } from 'react';
import { FaCarCrash, FaWrench } from 'react-icons/fa';
import {
  ASSET_STATUS,
  ASSET_STATUS_CLASSES,
  statusKeyOfContextGroup,
  statusKeyOfStage,
  type AssetStatusKey,
} from '../../../map/assetStatus';

/** A state tone: marker colour, pill classes, and a glyph or an icon (never colour alone). */
export interface ToneMeta {
  name: string;
  /** Marker / map colour. */
  hex: string;
  /** Tailwind pill classes. */
  pill: string;
  ring: string;
  glyph?: string;
  Icon?: ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean | 'true' }>;
}

// ST-00253 / ST-00244 (2026-10-19): an asset's colour comes from the ONE shared scheme,
// map/assetStatus (keys + light / dark tokens). The tones below that are asset states take their
// hex from it; weighbridge / idling / stopped stay for the chips that still name them.

// Offline / stale renders any tone hollow with a data-age badge (LS-09 §4).
export const OFFLINE_PILL = 'bg-transparent text-slate-500 ring-1 ring-inset ring-slate-400';
export const OFFLINE_GLYPH = '⌀';

// ── Tones (LS-09 §4 table) ──────────────────────────────────────────────────
// hex = marker / map colour; pill = Tailwind classes; glyph = text icon (or Icon).
export const TONES = {
  loaded: {
    name: 'In transit — loaded',
    hex: ASSET_STATUS.loaded.marker,
    pill: 'bg-blue-700 text-blue-50',
    ring: 'ring-blue-700',
    glyph: '▶',
  },
  empty: {
    name: 'In transit — empty',
    hex: ASSET_STATUS.empty.marker,
    pill: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-400',
    ring: 'ring-sky-400',
    glyph: '▷',
  },
  loading: {
    name: 'Loading at source',
    hex: ASSET_STATUS.at_source.marker,
    pill: 'bg-green-100 text-green-800',
    ring: 'ring-green-500',
    glyph: '⬒',
  },
  offloading: {
    name: 'Offloading at destination',
    hex: ASSET_STATUS.at_destination.marker,
    pill: 'bg-green-100 text-green-800',
    ring: 'ring-green-500',
    glyph: '⬓',
  },
  weighbridge: {
    name: 'At weighbridge',
    hex: '#0d9488',
    pill: 'bg-teal-100 text-teal-800',
    ring: 'ring-teal-500',
    glyph: '⚖',
  },
  idling: {
    name: 'Idling',
    hex: '#eab308',
    pill: 'bg-yellow-100 text-yellow-800',
    ring: 'ring-yellow-500',
    glyph: '◔',
  },
  stopped: {
    name: 'Stopped, unclassified',
    hex: '#dc2626',
    pill: 'bg-red-100 text-red-700',
    ring: 'ring-red-500',
    glyph: '■',
  },
  delay: {
    name: 'Delay',
    hex: ASSET_STATUS.delay.marker,
    pill: 'bg-orange-100 text-orange-800',
    ring: 'ring-orange-500',
    glyph: '⏳',
  },
  maintenance: {
    name: 'Under maintenance / out of service',
    hex: ASSET_STATUS.maintenance.marker,
    pill: 'bg-purple-100 text-purple-800',
    ring: 'ring-purple-500',
    Icon: FaWrench,
  },
  breakdown: {
    name: 'Breakdown',
    hex: ASSET_STATUS.breakdown.marker,
    pill: 'bg-rose-100 text-rose-800',
    ring: 'ring-rose-500',
    Icon: FaCarCrash,
  },
  parked: {
    name: 'Parked / at depot',
    hex: ASSET_STATUS.parked.marker,
    pill: 'bg-slate-200 text-slate-700',
    ring: 'ring-slate-400',
    glyph: 'P',
  },
  offline: {
    name: 'Offline / no data',
    hex: ASSET_STATUS.offline.marker,
    pill: OFFLINE_PILL,
    ring: 'ring-slate-400',
    glyph: OFFLINE_GLYPH,
  },
} satisfies Record<string, ToneMeta>;

export type ToneId = keyof typeof TONES;

// ── Axis A — motion (LS-04 §3.1) ────────────────────────────────────────────
export const MOTION_LABELS: Record<string, string> = {
  MOVING: 'moving',
  IDLING: 'idling',
  STOPPED: 'stopped',
  PARKED: 'parked',
  TOWED: 'towed',
};

// ── Axis B — operational context (LS-04 §3.2) ───────────────────────────────
export const CONTEXT_LABELS: Record<string, string> = {
  UNDER_MAINTENANCE: 'Under maintenance',
  OUT_OF_SERVICE: 'Out of service',
  STANDBY: 'Standby',
  AT_WEIGHBRIDGE: 'At weighbridge',
  AT_SOURCE: 'At source',
  AT_DESTINATION: 'At destination',
  IN_DELAY_ZONE: 'Delay zone',
  AT_DEPOT: 'At depot',
  EN_ROUTE_TO_SOURCE: 'To source',
  IN_TRANSIT_LOADED: 'In transit loaded',
  IN_TRANSIT_EMPTY: 'In transit empty',
  UNALLOCATED: 'Unallocated',
};

export const TRANSIT_CONTEXTS = ['EN_ROUTE_TO_SOURCE', 'IN_TRANSIT_LOADED', 'IN_TRANSIT_EMPTY'];

/** A Kanban column / summary tile: the contexts it gathers and its status key (map/assetStatus). */
export interface ContextGroup {
  id: string;
  label: string;
  contexts: string[];
  tone: AssetStatusKey;
}

const group = (id: string, label: string, contexts: string[]): ContextGroup => ({
  id,
  label,
  contexts,
  tone: statusKeyOfContextGroup(id),
});

/** Kanban columns / summary tiles, in the order a load moves through them. */
export const CONTEXT_GROUPS: ContextGroup[] = [
  group('to_source', 'To source', ['EN_ROUTE_TO_SOURCE']),
  group('at_source', 'At source', ['AT_SOURCE']),
  group('loaded', 'In transit loaded', ['IN_TRANSIT_LOADED']),
  group('weighbridge', 'At weighbridge', ['AT_WEIGHBRIDGE']),
  group('at_destination', 'At destination', ['AT_DESTINATION']),
  group('empty', 'In transit empty', ['IN_TRANSIT_EMPTY']),
  group('delay', 'Delay', ['IN_DELAY_ZONE']),
  group('depot', 'At depot', ['AT_DEPOT', 'STANDBY']),
  group('maintenance', 'Maintenance', ['UNDER_MAINTENANCE', 'OUT_OF_SERVICE']),
  group('unallocated', 'Unallocated', ['UNALLOCATED']),
];

export const contextGroupOf = (context: string | null | undefined): ContextGroup =>
  CONTEXT_GROUPS.find((g) => g.contexts.includes(context as string)) ||
  (CONTEXT_GROUPS[CONTEXT_GROUPS.length - 1] as ContextGroup);

// ── Overlays (LS-04 §3.2) ───────────────────────────────────────────────────
export const OVERLAY_META: Record<string, { label: string; glyph: string; className: string }> = {
  DEVIATING: {
    label: 'Deviating',
    glyph: '⤴',
    className: 'bg-pink-50 text-pink-800 ring-1 ring-inset ring-pink-500',
  },
  ALERT_OPEN: {
    label: 'Alert open',
    glyph: '!',
    className: 'bg-surface text-red-700 ring-2 ring-inset ring-red-500',
  },
  NO_DRIVER: {
    label: 'No driver',
    glyph: '?',
    className: 'bg-surface text-slate-600 border border-dashed border-slate-400',
  },
};

/** The two-axis status as the pages hold it (StatusBoardStatus and the trip / replay views). */
export interface StatusLike {
  context?: string | null;
  motion?: string | null;
  overlays?: readonly string[] | null;
}

const hasOverlay = (status: StatusLike | null | undefined, overlay: string): boolean =>
  Array.isArray(status?.overlays) && (status!.overlays as readonly string[]).includes(overlay);

export const isOffline = (status: StatusLike | null | undefined): boolean =>
  hasOverlay(status, 'OFFLINE');
export const isDeviating = (status: StatusLike | null | undefined): boolean =>
  hasOverlay(status, 'DEVIATING');

/** "At source · idling" (FR-STAT-002). */
export function combinedStatusLabel(status: StatusLike | null | undefined): string {
  const context = CONTEXT_LABELS[status?.context as string] || (CONTEXT_LABELS.UNALLOCATED as string);
  const motion = MOTION_LABELS[status?.motion as string];
  return motion ? `${context} · ${motion}` : context;
}

// ── Trip stages (LS-04 §4, m2-contracts §3.2) ───────────────────────────────
export const STAGE_LABELS: Record<string, string> = {
  PLANNED: 'Planned',
  DISPATCHED: 'Dispatched',
  ACKNOWLEDGED: 'Acknowledged',
  EN_ROUTE_TO_SOURCE: 'En route to source',
  AT_SOURCE: 'At source',
  IN_TRANSIT_LOADED: 'In transit loaded',
  AT_DESTINATION: 'At destination',
  IN_TRANSIT_EMPTY: 'In transit empty',
  COMPLETED: 'Completed',
  EXCEPTION_HOLD: 'Exception hold',
  RECONCILED: 'Reconciled',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  ABORTED: 'Aborted',
};

/** Stages whose pill speaks the asset status palette (the key the control room shows). */
const KEYED_STAGES = [
  'DISPATCHED',
  'ACKNOWLEDGED',
  'EN_ROUTE_TO_SOURCE',
  'AT_SOURCE',
  'IN_TRANSIT_LOADED',
  'AT_DESTINATION',
  'IN_TRANSIT_EMPTY',
  'EXCEPTION_HOLD',
];

/** Pill classes for a trip stage: active stages speak the state palette, the rest are neutral. */
export function stagePill(stage: string | null | undefined): string {
  if (stage && KEYED_STAGES.includes(stage)) return ASSET_STATUS_CLASSES[statusKeyOfStage(stage)].pill;
  if (stage === 'CANCELLED' || stage === 'ABORTED')
    return 'bg-slate-100 text-slate-500 line-through';
  if (stage === 'PLANNED') return 'bg-surface text-slate-600 ring-1 ring-inset ring-slate-300';
  return 'bg-slate-100 text-slate-700';
}

/** Stages a controller may set by hand (the override dialog). */
export const OVERRIDE_STAGES = [
  'AT_SOURCE',
  'IN_TRANSIT_LOADED',
  'AT_DESTINATION',
  'IN_TRANSIT_EMPTY',
  'COMPLETED',
  'EXCEPTION_HOLD',
];

export const ACTIVE_STAGES = [
  'DISPATCHED',
  'ACKNOWLEDGED',
  'EN_ROUTE_TO_SOURCE',
  'AT_SOURCE',
  'IN_TRANSIT_LOADED',
  'AT_DESTINATION',
  'IN_TRANSIT_EMPTY',
];

// ── Event severity (LS-09 §4: own scale, chips and list rows only) ─────────
export interface SeverityMeta {
  label: string;
  rank: number;
  className: string;
}

export const SEVERITY_META: Record<string, SeverityMeta> = {
  C: { label: 'Critical', rank: 5, className: 'bg-red-600 text-red-50' },
  H: { label: 'High', rank: 4, className: 'bg-orange-100 text-orange-800' },
  M: { label: 'Medium', rank: 3, className: 'bg-amber-100 text-amber-800' },
  L: { label: 'Low', rank: 2, className: 'bg-blue-50 text-blue-700' },
  I: { label: 'Info', rank: 1, className: 'bg-slate-100 text-slate-600' },
};

export const severityMeta = (s: string | null | undefined): SeverityMeta =>
  SEVERITY_META[s as string] || (SEVERITY_META.I as SeverityMeta);
